import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogIn, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* brand panel */}
      <div className="sf-grid-bg hidden lg:flex flex-col justify-between p-12 text-white">
        <Logo className="h-9" light />
        <div className="max-w-md">
          <h1 className="font-display text-4xl font-bold leading-tight">Multi-tenant workspace & visitor intelligence.</h1>
          <p className="text-blue-100/80 mt-4 text-lg">Visitor management and meeting room booking for premium co-working spaces — automated, secure and beautifully simple.</p>
          <div className="flex gap-2 mt-8">
            {["#10B981", "#2563EB", "#F59E0B", "#EF4444"].map((c) => (
              <span key={c} className="w-3 h-3 rounded-full" style={{ background: c }} />
            ))}
          </div>
        </div>
        <p className="text-xs text-blue-200/50">© {new Date().getFullYear()} Suvera Flow. Enterprise co-working platform.</p>
      </div>

      {/* form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md sf-fade-up">
          <div className="lg:hidden mb-8"><Logo className="h-8" /></div>
          <h2 className="font-display text-3xl font-bold text-navy-900">Sign in</h2>
          <p className="text-slate-500 mt-1">Access your Suvera Flow workspace.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1.5 h-11" required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" testId="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 h-11" required />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-11 bg-navy-900 hover:bg-navy-800 font-semibold">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <><LogIn size={18} className="mr-2" /> Sign in</>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
